import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./styles.module.css";

// Types
interface LineItem {
  id?: string; // Unique identifier for React key
  job_title: string;
  level_title: string;
  quantity: number;
  months_duration: number;
  effort: number;
  is_uae: boolean | null; // Allow null for unselected state
  job_family: string;
  sub_family: string;
  grade: number | null;
  missing_fields?: string[];
}

interface RateCardItem {
  id: number;
  job_title: string;
  level_title: string;
  job_family: string;
  sub_family: string;
  grade_level: number;
  ctc_midpoint_aed: number;
}

interface ParsedContent {
  finalAnswer: {
    line_items: LineItem[];
    total_count: number;
    rate_card: RateCardItem[];
    field_type?: "existing" | "new";
    flow_type?: string;
    error_message?: string;
  };
  question: string;
}

interface ManpowerCostCalculatorAgentProps {
  parsedContent: ParsedContent;
}

export default function ManpowerCostCalculatorAgent({
  parsedContent,
}: ManpowerCostCalculatorAgentProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Extract data from parsed content
  const {
    line_items = [],
    rate_card = [],
    field_type,
    error_message,
  } = parsedContent?.finalAnswer || {};
  const question = parsedContent?.question || "";

  // Separate valid and invalid line items and assign unique IDs
  const { validItems, invalidItems } = useMemo(() => {
    const valid: LineItem[] = [];
    const invalid: LineItem[] = [];

    line_items.forEach((item) => {
      // Add unique ID if not present and normalize is_uae (convert undefined to null)
      const itemWithId = {
        ...item,
        id: item.id || uuid(),
        is_uae: item.is_uae === undefined ? null : item.is_uae,
      };

      if (item.missing_fields && item.missing_fields.length > 0) {
        invalid.push(itemWithId);
      } else {
        valid.push(itemWithId);
      }
    });

    return { validItems: valid, invalidItems: invalid };
  }, [line_items]);

  // Get current agent and session info
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("sessionId");
  }, []);

  // State for edited line items (only valid items are editable)
  const [editedLineItems, setEditedLineItems] =
    useState<LineItem[]>(validItems);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [hasModifications, setHasModifications] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(true);
  const [showInvalidItems, setShowInvalidItems] = useState<boolean>(true);

  // Extract unique dropdown options from rate card
  const dropdownOptions = useMemo(() => {
    const jobFamilies = Array.from(
      new Set(rate_card.map((r) => r.job_family).filter(Boolean)),
    );
    const subFamilies = Array.from(
      new Set(rate_card.map((r) => r.sub_family).filter(Boolean)),
    );
    const grades = Array.from(
      new Set(rate_card.map((r) => r.grade_level)),
    ).sort((a, b) => a - b);

    return {
      jobFamilies,
      subFamilies,
      grades,
    };
  }, [rate_card]);

  // Get filtered sub-families based on selected job family
  const getSubFamiliesByJobFamily = useCallback(
    (jobFamily: string): string[] => {
      if (!jobFamily) return [];

      const filteredSubFamilies = rate_card
        .filter((r) => r.job_family === jobFamily)
        .map((r) => r.sub_family)
        .filter(Boolean);

      return Array.from(new Set(filteredSubFamilies));
    },
    [rate_card],
  );

  // Get filtered grades based on selected job family and sub family
  const getGradesByJobFamilyAndSubFamily = useCallback(
    (jobFamily: string, subFamily: string): number[] => {
      if (!jobFamily || !subFamily) return [];

      const filteredGrades = rate_card
        .filter((r) => r.job_family === jobFamily && r.sub_family === subFamily)
        .map((r) => r.grade_level)
        .filter((grade) => grade !== null && grade !== undefined);

      return Array.from(new Set(filteredGrades)).sort((a, b) => a - b);
    },
    [rate_card],
  );

  // Update a specific field in a line item
  const handleFieldChange = useCallback(
    (index: number, field: keyof LineItem, value: any) => {
      setHasModifications(true);
      setEditedLineItems((prev) => {
        const updated = [...prev];

        // Handle numeric fields - allow empty string for better UX while typing
        if (
          field === "quantity" ||
          field === "months_duration" ||
          field === "effort"
        ) {
          // Allow empty string or parse to number
          const numValue = value === "" ? 0 : parseInt(value, 10);
          // Store the parsed number (or 0 if empty)
          updated[index] = {
            ...updated[index],
            [field]: isNaN(numValue) ? 0 : numValue,
          };
        } else {
          updated[index] = { ...updated[index], [field]: value };
        }

        // Cascading dropdown logic
        if (field === "job_family") {
          // When job_family changes, reset sub_family and grade
          const validSubFamilies = getSubFamiliesByJobFamily(value as string);
          if (!validSubFamilies.includes(updated[index].sub_family)) {
            updated[index].sub_family = "";
            updated[index].grade = null; // Also reset grade
          }
        } else if (field === "sub_family") {
          // When sub_family changes, reset grade if it's not valid
          const validGrades = getGradesByJobFamilyAndSubFamily(
            updated[index].job_family,
            value as string,
          );
          if (
            updated[index].grade !== null &&
            !validGrades.includes(updated[index].grade!)
          ) {
            updated[index].grade = null;
          }
        }

        return updated;
      });
    },
    [getSubFamiliesByJobFamily, getGradesByJobFamilyAndSubFamily],
  );

  // Add a new empty row
  const handleAddRow = useCallback(() => {
    setHasModifications(true);
    const newRow: LineItem = {
      id: uuid(), // Assign unique ID for React key
      job_title: "",
      level_title: "",
      quantity: 1,
      months_duration: 1,
      effort: 100,
      is_uae: null, // Start with unselected state
      job_family: "",
      sub_family: "",
      grade: null,
    };
    setEditedLineItems((prev) => [...prev, newRow]);
  }, []);

  // Remove a row by index
  const handleRemoveRow = useCallback((index: number) => {
    setEditedLineItems((prev) => {
      // Prevent removing the last row - must have at least 1 row
      if (prev.length <= 1) {
        setError("At least one line item is required.");
        return prev;
      }
      setHasModifications(true);
      const updated = prev.filter((_, i) => i !== index);
      setError(null); // Clear any previous errors
      return updated;
    });
  }, []);

  // Check if all line items are complete and valid (for button state)
  const isFormComplete = useMemo(() => {
    // Must have at least one line item
    if (editedLineItems.length === 0) return false;

    // All line items must be complete and valid
    return editedLineItems.every(
      (item) =>
        item.job_title &&
        item.job_title.trim() !== "" &&
        item.level_title &&
        item.level_title.trim() !== "" &&
        item.quantity > 0 &&
        item.months_duration > 0 &&
        item.effort > 0 &&
        item.effort <= 100 &&
        item.is_uae !== null && // UAE National must be selected
        item.job_family &&
        item.job_family.trim() !== "" &&
        item.sub_family &&
        item.sub_family.trim() !== "",
    );
  }, [editedLineItems]);

  // Format user message for display in chat
  // const formatUserMessage = useCallback(() => {
  //   const totalCount = editedLineItems.length;
  //   const lines = [`✓ Calculate Estimate Request`, `\nLine Items Mapped: ${totalCount}\n`];

  //   editedLineItems.forEach((item, idx) => {
  //     lines.push(`${idx + 1}. ${item.job_title} (${item.level_title}) × ${item.quantity}`);
  //     lines.push(`   ${item.job_family} → ${item.sub_family} → Grade ${item.grade}`);
  //   });

  //   return lines.join("\n");
  // }, [editedLineItems]);

  // Submit the estimate calculation
  const handleCalculateEstimate = useCallback(async () => {
    // Button is disabled if form is incomplete, so this is a safety check
    if (!isFormComplete) {
      setError(
        "Please fill in all required fields before calculating estimate.",
      );
      return;
    }

    if (!currentAgent) {
      setError("Agent configuration not found");
      return;
    }

    try {
      setError(null);
      setIsSubmitted(true);

      const questionId = uuid();
      const timestamp = new Date().toISOString();

      // Prepare message payload with action and line items (excluding grade and id)
      const messagePayload = {
        action: "calculate_estimate",
        line_items: editedLineItems.map(({ id, grade, ...item }) => item),
      };

      // Prepare payload with JSON stringified message
      const payload = {
        agentId: currentAgent.id,
        message: JSON.stringify(messagePayload),
        questionId,
        timestamp,
        agentEndpoint: currentAgent.endpoint,
        sessionId,
      };

      dispatch(sendMessageToAgent(payload));
    } catch (err) {
      console.error("Error submitting estimate request:", err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to calculate estimate. Please try again.";
      setError(errorMessage);
      setIsSubmitted(false);
    }
  }, [editedLineItems, currentAgent, sessionId, dispatch, isFormComplete]);

  // Check if any line item has validation errors (for highlighting)
  const isLineItemIncomplete = useCallback((item: LineItem): boolean => {
    return (
      !item.job_family ||
      !item.sub_family ||
      item.is_uae === null ||
      item.quantity <= 0 ||
      item.months_duration <= 0 ||
      item.effort <= 0 ||
      item.effort > 100
    );
  }, []);

  // Format field name for display (e.g., "job_title" -> "Job Title")
  const formatFieldName = useCallback((fieldName: string): string => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerIcon}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#00A3A3">
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Box>
        <Typography variant="subtitle2" className={styles.headerText}>
          MANPOWER COST CALCULATOR
        </Typography>
      </Box>

      {/* Question/Instructions */}
      {/* {question && (
        <Typography className={styles.question}>{question}</Typography>
      )} */}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* API Error Message - General validation errors from backend */}
      {error_message && showErrorMessage && (
        <Alert
          severity="error"
          onClose={() => setShowErrorMessage(false)}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {error_message}
          </Typography>
        </Alert>
      )}

      {/* Invalid Line Items Section - Items with missing fields */}
      {invalidItems.length > 0 && showInvalidItems && (
        <Box className={styles.invalidItemsSection}>
          <Alert
            severity="error"
            onClose={() => setShowInvalidItems(false)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              The following line item(s) could not be processed due to missing
              information:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: "20px" }}>
              {invalidItems.map((item, index) => (
                <li key={index} style={{ marginBottom: "8px" }}>
                  <Typography variant="body2" component="span">
                    <strong>
                      {item.job_title || "Unknown Job Title"}
                      {item.level_title ? ` (${item.level_title})` : ""}
                    </strong>
                    {" - Missing: "}
                    {item.missing_fields
                      ?.map((field) => formatFieldName(field))
                      .join(", ")}
                  </Typography>
                </li>
              ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1.5, fontStyle: "italic" }}>
              Please contact the Retail Rewards team to update these records.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Add Row Button */}
      <Box className={styles.addRowContainer}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          disabled={agentLoading || isSubmitted}
          className={styles.addRowButton}
        >
          Add Line Item
        </Button>
        <Typography className={styles.lineItemCount}>
          {editedLineItems.length} line item
          {editedLineItems.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* Table Header */}
      <Box className={styles.tableHeader}>
        <Box className={`${styles.headerCell} ${styles.cellJobTitle}`}>
          JOB TITLE
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellLevelTitle}`}>
          LEVEL TITLE
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellQty}`}>QTY</Box>
        <Box className={`${styles.headerCell} ${styles.cellMonths}`}>
          MONTHS
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellEffort}`}>
          EFFORT%
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellUaeNational}`}>
          UAE NATIONAL
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellJobFamily}`}>
          JOB FAMILY
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellSubFamily}`}>
          SUB FAMILY
        </Box>
        <Box className={`${styles.headerCell} ${styles.cellActions}`}>
          ACTIONS
        </Box>
      </Box>

      {/* Table Rows */}
      <Box className={styles.tableBody}>
        {editedLineItems.map((item, index) => {
          const isIncomplete = isLineItemIncomplete(item);
          const availableSubFamilies = getSubFamiliesByJobFamily(
            item.job_family,
          );
          const availableGrades = getGradesByJobFamilyAndSubFamily(
            item.job_family,
            item.sub_family,
          );

          return (
            <Box
              key={item.id || index}
              className={`${styles.tableRow} ${isIncomplete ? styles.rowIncomplete : ""}`}
            >
              {/* Job Title */}
              <Box className={`${styles.cell} ${styles.cellJobTitle}`}>
                <TextField
                  fullWidth
                  size="small"
                  value={item.job_title}
                  onChange={(e) =>
                    handleFieldChange(index, "job_title", e.target.value)
                  }
                  disabled={agentLoading || isSubmitted}
                  variant="outlined"
                  placeholder="Enter job title"
                  inputProps={{
                    style: {
                      cursor: "text",
                      caretColor: "#d32f2f",
                    },
                    "aria-label": "Job Title",
                  }}
                  InputProps={{
                    style: {
                      cursor: "text",
                    },
                  }}
                  className={`${styles.input} ${!item.job_title || item.job_title.trim() === "" ? styles.inputRequiredHighlight : ""}`}
                />
              </Box>

              {/* Level Title */}
              <Box className={`${styles.cell} ${styles.cellLevelTitle}`}>
                <TextField
                  fullWidth
                  size="small"
                  value={item.level_title}
                  onChange={(e) =>
                    handleFieldChange(index, "level_title", e.target.value)
                  }
                  disabled={agentLoading || isSubmitted}
                  variant="outlined"
                  placeholder="Enter level title"
                  inputProps={{
                    style: {
                      cursor: "text",
                      caretColor: "#d32f2f",
                    },
                    "aria-label": "Level Title",
                  }}
                  InputProps={{
                    style: {
                      cursor: "text",
                    },
                  }}
                  className={`${styles.input} ${!item.level_title || item.level_title.trim() === "" ? styles.inputRequiredHighlight : ""}`}
                />
              </Box>

              {/* Quantity */}
              <Box className={`${styles.cell} ${styles.cellQty}`}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={item.quantity === 0 ? "" : item.quantity}
                  onChange={(e) =>
                    handleFieldChange(index, "quantity", e.target.value)
                  }
                  disabled={agentLoading || isSubmitted}
                  variant="outlined"
                  className={`${styles.input} ${item.quantity <= 0 ? styles.inputHighlight : ""}`}
                  inputProps={{
                    min: 1,
                    style: { cursor: "text", caretColor: "#d32f2f" },
                  }}
                  InputProps={{
                    style: { cursor: "text" },
                  }}
                  placeholder="Qty"
                />
              </Box>

              {/* Months */}
              <Box className={`${styles.cell} ${styles.cellMonths}`}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={item.months_duration === 0 ? "" : item.months_duration}
                  onChange={(e) =>
                    handleFieldChange(index, "months_duration", e.target.value)
                  }
                  disabled={agentLoading || isSubmitted}
                  variant="outlined"
                  className={`${styles.input} ${item.months_duration <= 0 ? styles.inputHighlight : ""}`}
                  inputProps={{
                    min: 1,
                    style: { cursor: "text", caretColor: "#d32f2f" },
                  }}
                  InputProps={{
                    style: { cursor: "text" },
                  }}
                  placeholder="Months"
                />
              </Box>

              {/* Effort % */}
              <Box className={`${styles.cell} ${styles.cellEffort}`}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={item.effort === 0 ? "" : item.effort}
                  onChange={(e) =>
                    handleFieldChange(index, "effort", e.target.value)
                  }
                  disabled={agentLoading || isSubmitted}
                  variant="outlined"
                  className={`${styles.input} ${item.effort <= 0 || item.effort > 100 ? styles.inputHighlight : ""}`}
                  inputProps={{
                    min: 1,
                    max: 100,
                    style: { cursor: "text", caretColor: "#d32f2f" },
                  }}
                  InputProps={{
                    style: { cursor: "text" },
                  }}
                  placeholder="%"
                />
              </Box>

              {/* UAE National */}
              <Box className={`${styles.cell} ${styles.cellUaeNational}`}>
                <FormControl fullWidth size="small">
                  <Select
                    value={
                      item.is_uae === null ? "" : item.is_uae ? "Yes" : "No"
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      handleFieldChange(
                        index,
                        "is_uae",
                        value === "" ? null : value === "Yes",
                      );
                    }}
                    disabled={agentLoading || isSubmitted}
                    displayEmpty
                    className={`${styles.select} ${item.is_uae === null ? styles.selectHighlight : ""}`}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Job Family */}
              <Box className={`${styles.cell} ${styles.cellJobFamily}`}>
                <FormControl fullWidth size="small">
                  <Select
                    value={item.job_family}
                    onChange={(e) =>
                      handleFieldChange(index, "job_family", e.target.value)
                    }
                    disabled={agentLoading || isSubmitted}
                    displayEmpty
                    className={`${styles.select} ${!item.job_family ? styles.selectHighlight : ""}`}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {dropdownOptions.jobFamilies.map((family) => (
                      <MenuItem key={family} value={family}>
                        {family}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Sub Family */}
              <Box className={`${styles.cell} ${styles.cellSubFamily}`}>
                <FormControl fullWidth size="small">
                  <Select
                    value={item.sub_family}
                    onChange={(e) =>
                      handleFieldChange(index, "sub_family", e.target.value)
                    }
                    disabled={
                      agentLoading ||
                      isSubmitted ||
                      !item.job_family ||
                      availableSubFamilies.length === 0
                    }
                    displayEmpty
                    className={`${styles.select} ${!item.sub_family ? styles.selectHighlight : ""}`}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {availableSubFamilies.map((subFamily) => (
                      <MenuItem key={subFamily} value={subFamily}>
                        {subFamily}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Actions - Delete Button */}
              <Box className={`${styles.cell} ${styles.cellActions}`}>
                <Tooltip title="Delete line item" arrow>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveRow(index)}
                      disabled={
                        agentLoading ||
                        isSubmitted ||
                        editedLineItems.length <= 1
                      }
                      className={styles.deleteButton}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Submit Button */}
      <Box className={styles.footer}>
        <Button
          variant="contained"
          onClick={handleCalculateEstimate}
          disabled={
            agentLoading ||
            isSubmitted ||
            !isFormComplete ||
            (field_type === "existing" && !hasModifications)
          }
          className={styles.submitButton}
        >
          Calculate Estimate →
        </Button>
      </Box>
    </Box>
  );
}
